/**
 * Componente visual do node de Horário Comercial
 * Exibe resumo dos horários configurados e duas saídas: Aberto e Fechado
 * 
 * RELEASE: 1.0.0 - 2025-01-22 - Novo node
 */

import { memo, useMemo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { FlowNodeToolbar } from './FlowNodeToolbar';
import { BusinessHoursConfig, FlowEdge } from '@/types/flows';
import { cn } from '@/lib/utils';

// Mapeamento de dias para português
const dayLabels: Record<string, string> = {
  sunday: 'Dom',
  monday: 'Seg',
  tuesday: 'Ter',
  wednesday: 'Qua',
  thursday: 'Qui',
  friday: 'Sex',
  saturday: 'Sáb',
};

// Ordem dos dias da semana
const daysOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

/**
 * Node visual de Horário Comercial
 * Possui duas saídas fixas: "Aberto" e "Fechado"
 */
export const BusinessHoursNode = memo(({ data, id, selected }: NodeProps) => {
  const config = data.config as BusinessHoursConfig | undefined;
  const edges = (data.edges || []) as FlowEdge[];
  
  // Verifica quais handles estão conectados
  const connectedHandles = useMemo(() => {
    const connected = new Set<string>();
    edges.forEach(edge => {
      if (edge.source === id && edge.sourceHandle) {
        connected.add(edge.sourceHandle);
      }
    });
    return connected;
  }, [edges, id]);
  
  // Gera resumo dos horários configurados
  const scheduleSummary = useMemo(() => {
    if (!config?.schedule) return 'Não configurado';
    
    const enabledDays: string[] = [];
    const schedule = config.schedule;
    
    // Agrupa dias consecutivos com mesmo horário
    let currentGroup: { days: string[]; time: string } | null = null;
    
    for (const day of daysOrder) {
      const dayConfig = schedule[day as keyof typeof schedule];
      if (dayConfig?.enabled) {
        const timeStr = `${dayConfig.start_time}-${dayConfig.end_time}`;
        
        if (currentGroup && currentGroup.time === timeStr) {
          currentGroup.days.push(dayLabels[day]);
        } else {
          if (currentGroup) {
            enabledDays.push(formatGroup(currentGroup));
          }
          currentGroup = { days: [dayLabels[day]], time: timeStr };
        }
      } else {
        if (currentGroup) {
          enabledDays.push(formatGroup(currentGroup));
          currentGroup = null;
        }
      }
    }
    
    if (currentGroup) {
      enabledDays.push(formatGroup(currentGroup));
    }
    
    return enabledDays.length > 0 ? enabledDays.join(', ') : 'Nenhum dia ativo';
  }, [config]);
  
  // Formata grupo de dias
  function formatGroup(group: { days: string[]; time: string }): string {
    if (group.days.length === 1) {
      return `${group.days[0]} ${group.time}`;
    }
    return `${group.days[0]}-${group.days[group.days.length - 1]} ${group.time}`;
  }

  return (
    <div 
      className={cn(
        "bg-card border-2 rounded-lg shadow-lg min-w-[220px] transition-all",
        selected ? "border-primary ring-2 ring-primary/20" : "border-blue-600/50"
      )}
    >
      {/* Toolbar flutuante quando selecionado */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType="business_hours"
        onEdit={data.onEdit as (() => void) | undefined}
        onDuplicate={(data as any).onDuplicate as (() => void) | undefined}
        onDelete={data.onDelete as (() => void) | undefined}
        onTestFromHere={(data as any).onTestFromHere as (() => void) | undefined}
        isVisible={selected || false}
      />
      
      {/* Handle de entrada (topo) */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !bg-blue-500 !border-2 !border-background"
      />
      
      {/* Header */}
      <div className="flex items-center gap-2 p-3 border-b bg-blue-600/10">
        <Clock className="h-4 w-4 text-blue-600" />
        <span className="font-semibold text-sm">{(data as any).config?.nickname || 'Horário Comercial'}</span>
      </div>
      
      {/* Resumo dos horários */}
      <div className="p-3 space-y-2">
        <p className="text-xs text-muted-foreground line-clamp-2">
          {scheduleSummary}
        </p>
        {config?.timezone && (
          <Badge variant="outline" className="text-xs">
            {config.timezone}
          </Badge>
        )}
      </div>
      
      {/* Saídas: Aberto / Fechado */}
      <div className="p-2 space-y-2 border-t">
        {/* Saída: Aberto */}
        <div 
          className={cn(
            "relative flex items-center justify-between p-2 rounded border transition-colors",
            connectedHandles.has('open') 
              ? "bg-green-500/10 border-green-500/30" 
              : "bg-green-500/5 border-dashed border-green-500/20"
          )}
        >
          <span className="text-xs font-medium text-green-600 dark:text-green-400">
            ✓ Aberto
          </span>
          <Handle
            type="source"
            position={Position.Right}
            id="open"
            className={cn(
              "!w-3 !h-3 !border-2 !border-background !right-[-6px]",
              connectedHandles.has('open') ? "!bg-green-500" : "!bg-green-300 animate-pulse"
            )}
          />
        </div>
        
        {/* Saída: Fechado */}
        <div 
          className={cn(
            "relative flex items-center justify-between p-2 rounded border transition-colors",
            connectedHandles.has('closed') 
              ? "bg-red-500/10 border-red-500/30" 
              : "bg-red-500/5 border-dashed border-red-500/20"
          )}
        >
          <span className="text-xs font-medium text-red-600 dark:text-red-400">
            ✗ Fechado
          </span>
          <Handle
            type="source"
            position={Position.Right}
            id="closed"
            className={cn(
              "!w-3 !h-3 !border-2 !border-background !right-[-6px]",
              connectedHandles.has('closed') ? "!bg-red-500" : "!bg-red-300 animate-pulse"
            )}
          />
        </div>
      </div>
    </div>
  );
});

BusinessHoursNode.displayName = 'BusinessHoursNode';
